import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { FileUp, Plus, RefreshCw, Settings2 } from 'lucide-react'
import {
  Button,
  EmptyState,
  groupRows,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  useTableGrouping,
  ViewToggle,
  type TableGroup,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import {
  useMoveStage,
  useRegulatoryStats,
  useReorderStages,
  useRequestList,
  useStages,
} from './api/queries'
import type { Assignee, Category, Priority, RequestSummary, RequestType } from './api/types'
import {
  accentForHex,
  CATEGORY_OPTIONS,
  CATEGORY_SHORT,
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
} from './utils/meta'
import { RegulatoryKanban } from './components/RegulatoryKanban'
import { RegulatoryTable } from './components/RegulatoryTable'
import { NewCardDrawer } from './components/NewCardDrawer'
import { StageManagerDrawer } from './components/StageManagerDrawer'
import { RequestDocsDrawer } from './components/RequestDocsDrawer'
import { DOC_REQUEST_CREATE_ROLES } from './api/doc-requests'

type View = 'active' | 'archive'
type Display = 'kanban' | 'table'

const DISPLAY_OPTIONS = [
  { value: 'kanban' as const, label: 'Kanban' },
  { value: 'table' as const, label: 'Table' },
]

const GROUP_BY_OPTIONS = [
  { value: 'stage', label: 'Group by stage' },
  { value: 'assignee', label: 'Group by assignee' },
  { value: 'category', label: 'Group by category' },
  { value: 'priority', label: 'Group by priority' },
  { value: 'none', label: 'No grouping' },
]

const PRIORITY_GROUP_ORDER: Priority[] = ['urgent', 'high', 'medium', 'low']

const VIEW_OPTIONS = [
  { value: 'active' as const, label: 'Active' },
  { value: 'archive' as const, label: 'Archive' },
]

const TITLE: Record<RequestType, string> = {
  customer: 'Regulatory — Customer',
  vendor: 'Regulatory — Vendor',
}

const SUBTITLE: Record<RequestType, string> = {
  customer: 'Customer requests — COA, SDS/TDS, compliance, spec dossiers',
  vendor: 'Vendor requests — documentation, country-of-origin, change notifications',
}

/**
 * Shared regulatory board, fixed to one `requestType`. Rendered by the two
 * sibling modules (regulatory-customer / regulatory-vendor). Customer and
 * vendor are fully separate destinations with their own stage sets and
 * activities — this component never mixes the two.
 */
const BOARD_PATH: Record<RequestType, string> = {
  customer: '/regulatory/customer',
  vendor: '/regulatory/vendor',
}

export function RegulatoryBoard({ requestType }: { requestType: RequestType }) {
  const canEdit = useHasAnyPermission(['admin', 'operations', 'purchasing', 'regulatory'])
  const canRequestDocs = useHasAnyPermission(DOC_REQUEST_CREATE_ROLES) && requestType === 'vendor'
  const navigate = useNavigate()
  const [docsOpen, setDocsOpen] = useState(false)

  const [view, setView] = useState<View>('active')
  const [display, setDisplay] = useState<Display>('kanban')
  const { groupBy, setGroupBy, collapsedGroups, toggleGroup } = useTableGrouping(
    `regulatory-${requestType}`,
    'stage',
  )
  const [assignee, setAssignee] = useState<'' | Assignee | 'unassigned'>('')
  const [category, setCategory] = useState<'' | Category>('')
  const [priority, setPriority] = useState<'' | Priority>('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [stageMgrOpen, setStageMgrOpen] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openRequest = (id: string) =>
    navigate({
      to: `${BOARD_PATH[requestType]}/$requestId`,
      params: { requestId: id },
    })

  const isArchive = view === 'archive'
  const stats = useRegulatoryStats()
  const stages = useStages(requestType)
  const move = useMoveStage()
  const reorder = useReorderStages()

  const list = useRequestList({
    request_type: requestType,
    ...(isArchive ? { is_archived: true } : {}),
    ...(assignee ? { assigned_to: assignee } : {}),
    ...(category ? { category } : {}),
    ...(priority ? { priority } : {}),
    ...(overdueOnly ? { overdue_only: true } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  })

  const items = useMemo(() => list.data?.items ?? [], [list.data])
  const stageItems = useMemo(
    () => [...(stages.data?.items ?? [])].sort((a, b) => a.sequence - b.sequence),
    [stages.data],
  )

  // Table-view grouping. Stage uses the full ordered stage list so empty
  // stages still render a `(0)` header (matches the kanban's always-visible
  // columns). assignee/category/priority group the same flat rows.
  const tableGroups = useMemo<TableGroup<RequestSummary>[] | undefined>(() => {
    if (display !== 'table' || isArchive || groupBy === 'none') return undefined
    if (groupBy === 'stage') {
      const nameById = new Map(stageItems.map((s) => [s.id, s] as const))
      return groupRows(items, {
        keyOf: (c) => c.stage_id ?? '',
        groupOrder: stageItems.map((s) => s.id),
        labelOf: (k) => nameById.get(k)?.name ?? '(no stage)',
        accentOf: (k) => accentForHex(nameById.get(k)?.color ?? null),
      })
    }
    if (groupBy === 'assignee') {
      return groupRows(items, {
        keyOf: (c) => c.assigned_to_name ?? '',
        labelOf: (k) => (k === '__none__' ? 'Unassigned' : k),
      })
    }
    if (groupBy === 'category') {
      return groupRows(items, {
        keyOf: (c) => c.category,
        labelOf: (k) => CATEGORY_SHORT[k as Category] ?? k,
      })
    }
    // priority
    return groupRows(items, {
      keyOf: (c) => c.priority,
      groupOrder: PRIORITY_GROUP_ORDER,
      labelOf: (k) => PRIORITY_LABELS[k as Priority] ?? k,
    })
  }, [display, isArchive, groupBy, items, stageItems])

  const openCount =
    requestType === 'customer'
      ? stats.data?.open_customer
      : stats.data?.open_vendor

  return (
    <div className="flex h-full w-full flex-col bg-canvas">
      <PageHeaderBand title={TITLE[requestType]} description={SUBTITLE[requestType]}>
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open" value={openCount ?? 0} />
            <MetricCard variant="inline" label="Overdue" value={stats.data?.overdue ?? 0} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      <Toolbar>
        <SearchInput
          ref={searchRef}
          containerClassName="flex-1 max-w-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, partner, product…"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <ViewToggle<View> options={VIEW_OPTIONS} value={view} onChange={setView} />
          {!isArchive ? (
            <ViewToggle<Display>
              options={DISPLAY_OPTIONS}
              value={display}
              onChange={setDisplay}
              aria-label="Display mode"
            />
          ) : null}
          {!isArchive && display === 'table' ? (
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              aria-label="Group rows by"
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          ) : null}
          <Select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value as typeof assignee)}
            aria-label="Filter by assignee"
          >
            <option value="">All assignees</option>
            <option value="frank">Frank</option>
            <option value="shamola">Shamola</option>
            <option value="unassigned">Unassigned</option>
          </Select>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as typeof category)}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value as typeof priority)}
            aria-label="Filter by priority"
          >
            <option value="">All priorities</option>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-role-toolbar text-secondary">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
            />
            Overdue only
          </label>
        </ToolbarLeft>
        <ToolbarRight>
          <Button variant="ghost" size="sm" onClick={() => list.refetch()} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canEdit && !isArchive ? (
            <Button variant="secondary" size="sm" onClick={() => setStageMgrOpen(true)}>
              <Settings2 className="mr-1 h-4 w-4" />
              Stages
            </Button>
          ) : null}
          {canRequestDocs ? (
            <Button variant={canEdit ? 'secondary' : 'primary'} size="sm" onClick={() => setDocsOpen(true)}>
              <FileUp className="mr-1 h-4 w-4" />
              Request documents
            </Button>
          ) : null}
          {canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              New Card
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {isArchive ? (
          items.length === 0 ? (
            <EmptyState
              title="No archived cards"
              description="Completed and cancelled cards land here."
            />
          ) : (
            <ArchiveList items={items} onSelect={(c) => openRequest(c.id)} />
          )
        ) : display === 'table' ? (
          <RegulatoryTable
            rows={items}
            stages={stageItems}
            groups={tableGroups}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
            onSelect={(c) => openRequest(c.id)}
          />
        ) : (
          // Active kanban — columns show even when empty so the
          // request_type's distinct stages are always visible.
          <RegulatoryKanban
            requestType={requestType}
            readOnly={!canEdit}
            stages={stages.data?.items ?? []}
            items={items}
            onSelect={(c) => openRequest(c.id)}
            onMove={(card, targetStageId) =>
              move.mutate(
                { id: card.id, targetStageId },
                { onError: (e) => toast.error(String(e)) },
              )
            }
            onReorder={(stageIds) =>
              reorder.mutate(stageIds.map((id, i) => ({ id, sequence: (i + 1) * 10 })))
            }
          />
        )}
      </div>

      <NewCardDrawer
        open={newOpen}
        onOpenChange={setNewOpen}
        lockedRequestType={requestType}
        onCreated={(id) => openRequest(id)}
      />
      <RequestDocsDrawer open={docsOpen} onOpenChange={setDocsOpen} onOpenCard={(id) => openRequest(id)} />
      <StageManagerDrawer
        open={stageMgrOpen}
        onOpenChange={setStageMgrOpen}
        requestType={requestType}
      />
    </div>
  )
}

function ArchiveList({
  items,
  onSelect,
}: {
  items: RequestSummary[]
  onSelect: (c: RequestSummary) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      {items.map((c) => (
        <button
          key={c.id}
          onClick={() => onSelect(c)}
          className="flex items-center gap-3 rounded-sm border border-border-subtle bg-surface px-3 py-2 text-left hover:border-border-strong"
        >
          <span className="text-role-data-primary flex-1 truncate">{c.title}</span>
          <span className="text-role-metadata text-tertiary">{c.partner_name}</span>
        </button>
      ))}
    </div>
  )
}
